"use strict";

/**
 * WEB-4818: the Key Handover schedule rules shared by the settings page and the staff
 * booking wizard (SSKeyHandoverService).
 *
 * The server (WEB-4815) is the source of truth; this mirrors it, and the DMS Flutter app
 * (WEB-4816) applies the same rules, so all three clients offer the same days and slots.
 *
 *   - Times are stored as UTC `HH:mm` and shown in the PROJECT's timezone
 *     (`key_handover_settings.value.mobile_app_time_zone.id`), not the browser's, so staff
 *     in another timezone see the hours the site is actually staffed for. With no
 *     timezone set (or moment-timezone not loaded) it falls back to the browser's zone,
 *     which is how the page behaved before.
 *   - A date resolves special day → weekday → General, and each of units per slot,
 *     duration and max per day falls back on its own (see resolveDay()).
 *   - A range whose end is at or before its start runs past midnight; the slots after
 *     midnight belong to the start date's list but fall on the next day.
 */
class SSKeyHandoverScheduleService {

    constructor(SSDateService) {
        const self = this;

        self.SSDateService = SSDateService;

        self.DEFAULT_SLOT_PERIOD = 60;
    }

    // ---------- Timezone ----------

    /**
     * The IANA id the times on this project are read and written in, or null for the
     * browser's own zone.
     *
     * @param {Object} value - key_handover_settings.value
     * @returns {string|null}
     */
    timeZoneId(value) {
        let zone = value && value.mobile_app_time_zone;
        let id = zone && typeof zone === 'object' ? zone.id : zone;

        if (!id || typeof moment === 'undefined' || !moment.tz || !moment.tz.zone(id)) {
            return null;
        }

        return id;
    }

    /**
     * A wall-clock moment in the zone, or in the browser's zone when there is none.
     *
     * @param {string} text - Date-time text.
     * @param {string} format - moment format of `text`.
     * @param {string|null} zoneId
     */
    _inZone(text, format, zoneId) {
        return zoneId ? moment.tz(text, format, true, zoneId) : moment(text, format, true);
    }

    /**
     * Stored UTC `HH:mm` → the project's `HH:mm` on `onDate` (the offset depends on the
     * date when the zone has daylight saving). Returns the input when it does not parse.
     *
     * @param {string} hhmm
     * @param {string|null} zoneId
     * @param {moment|string} [onDate] - Defaults to today.
     */
    utcToProjectHHmm(hhmm, zoneId, onDate) {
        if (!hhmm) return hhmm;

        let day = onDate ? moment(onDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
        let utc = moment.utc(day + ' ' + hhmm.substring(0, 5), 'YYYY-MM-DD HH:mm', true);

        if (!utc.isValid()) return hhmm;

        return (zoneId ? utc.tz(zoneId) : utc.local()).format('HH:mm');
    }

    /**
     * The project's `HH:mm` → stored UTC `HH:mm` on `onDate`. Inverse of utcToProjectHHmm().
     */
    projectToUtcHHmm(hhmm, zoneId, onDate) {
        if (!hhmm) return hhmm;

        let day = onDate ? moment(onDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
        let wall = this._inZone(day + ' ' + hhmm.substring(0, 5), 'YYYY-MM-DD HH:mm', zoneId);

        if (!wall.isValid()) return hhmm;

        return wall.utc().format('HH:mm');
    }

    /**
     * A server `YYYY-MM-DD HH:mm:ss` (UTC) → a moment holding the project's wall clock.
     *
     * The result is a plain (browser-zone) moment carrying the project's calendar fields,
     * which is the shape the wizard has always compared and formatted.
     */
    utcToProjectMoment(value, zoneId) {
        if (!value) return value;

        let utc = moment.utc(value);
        if (!utc.isValid()) return value;

        let wall = zoneId ? utc.tz(zoneId) : utc.local();

        return moment(wall.format('YYYY-MM-DD HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');
    }

    /**
     * A project wall-clock `YYYY-MM-DD HH:mm:ss` → the UTC `YYYY-MM-DD HH:mm:ss` to send.
     */
    projectToUtcDateTime(value, zoneId) {
        if (!value) return value;

        let wall = this._inZone(value, 'YYYY-MM-DD HH:mm:ss', zoneId);
        if (!wall.isValid()) return null;

        return wall.utc().format('YYYY-MM-DD HH:mm:ss');
    }

    /**
     * "Now" on the project's clock, as a plain moment (see utcToProjectMoment()).
     */
    nowInProject(zoneId) {
        return this.utcToProjectMoment(moment.utc().format('YYYY-MM-DD HH:mm:ss'), zoneId);
    }

    // ---------- Settings values ----------

    /**
     * An optional count as an int, or null when it is not set. 0 is a real value.
     */
    optionalCount(value) {
        if (value === null || value === undefined || value === '') return null;

        let parsed = parseInt(value);

        return isNaN(parsed) ? null : parsed;
    }

    /**
     * The ranges that carry both times. A blank row is how the editor shows "inherit the
     * weekday's ranges", so it is not a range of its own.
     */
    filledSlots(slots) {
        return (slots || []).filter(x => x && x.start_time && x.end_time);
    }

    /**
     * The weekday row for `date`, matched by name as the server does, then by id (0 = Sunday).
     * Never by array position — the rows are not guaranteed to start on Sunday.
     */
    weekDayFor(value, date) {
        if (!value || !value.week_days || !date) return null;

        let day = moment(date);
        let name = day.locale('en').format('dddd').toLowerCase();

        return value.week_days.find(x => x && x.name && x.name.toLowerCase() == name)
            || value.week_days.find(x => x && x.id !== undefined && x.id !== null && parseInt(x.id) == day.day())
            || null;
    }

    /**
     * The special-day row for `date`, if any. Both stored shapes (`YYYY-MM-DD 12:00:00` and
     * the on-screen `DD/MM/YYYY`) are matched.
     */
    specialDayFor(value, date) {
        if (!value || !value.special_days || !date) return null;

        const self = this;

        let key = moment(date).format('YYYY-MM-DD');

        return value.special_days.find(x => x && self.SSDateService.parseSettingsDate(x.date) == key) || null;
    }

    /**
     * WEB-4815 resolution for one project-zone date — the rule the server applies.
     *
     *   - Special day Off → closed.
     *   - Special day On → its own ranges, or the weekday's when it has none (only an Open
     *     weekday has any); each value from the special day, then the weekday, then General.
     *   - No special day → the weekday; Closed means closed, otherwise each value from the
     *     weekday, then General.
     *
     * A Closed weekday contributes nothing — the settings page does not store its ranges or
     * values — so stale ones are never honoured here.
     *
     * @param {Object} value - key_handover_settings.value (slot times still in stored UTC).
     * @param {moment|string} date
     * @returns {{open: boolean, slots: Array, number_of_tables: ?number, slot_period: ?number, max_units_per_day: ?number}}
     */
    resolveDay(value, date) {
        const self = this;

        let closedDay = {open: false, slots: [], number_of_tables: null, slot_period: null, max_units_per_day: null};

        if (!value) return closedDay;

        let weekDay = self.weekDayFor(value, date);
        let openWeekDay = weekDay && !self.isClosed(weekDay) ? weekDay : null;
        let special = self.specialDayFor(value, date);

        let pick = function (key) {
            let sources = [special, openWeekDay, value];
            for (let i = 0; i < sources.length; i++) {
                let v = sources[i] ? self.optionalCount(sources[i][key]) : null;
                if (v !== null) return v;
            }
            return null;
        };

        if (special) {
            if (self.isSpecialDayOff(special)) return closedDay;

            let own = self.filledSlots(special.slots);
            let slots = own.length ? own : (openWeekDay ? self.filledSlots(openWeekDay.slots) : []);

            return {
                open: slots.length > 0,
                slots: slots,
                number_of_tables: pick('number_of_tables'),
                slot_period: pick('slot_period'),
                max_units_per_day: pick('max_units_per_day')
            };
        }

        if (!openWeekDay) return closedDay;

        return {
            open: true,
            slots: self.filledSlots(openWeekDay.slots),
            number_of_tables: pick('number_of_tables'),
            slot_period: pick('slot_period'),
            max_units_per_day: pick('max_units_per_day')
        };
    }

    /**
     * `closed` arrives as 1/0, true/false, or absent. Absent means `whenAbsent`: Open for a
     * weekday, as it always has, and Off for a special day — a row has to say it is open.
     */
    isClosed(entry, whenAbsent = false) {
        if (!entry) return true;

        if (entry.closed === undefined || entry.closed === null) return whenAbsent;

        return entry.closed === true || entry.closed == 1;
    }

    isSpecialDayOff(entry) {
        return this.isClosed(entry, true);
    }

    /**
     * The bookable slots of one project-zone date, each with its real start and end.
     *
     * A range whose end is at or before its start runs past midnight (22:00–02:00): the
     * slots after midnight stay in this date's list but carry the next day's date, which is
     * the date the server books them on.
     *
     * @param {Object} value - key_handover_settings.value (slot times in stored UTC).
     * @param {moment|string} date
     * @param {string|null} zoneId
     * @returns {Array<{start: moment, end: moment}>}
     */
    buildSlots(value, date, zoneId) {
        const self = this;

        let resolved = self.resolveDay(value, date);
        if (!resolved.open) return [];

        let period = resolved.slot_period && resolved.slot_period > 0 ? resolved.slot_period : self.DEFAULT_SLOT_PERIOD;
        let day = moment(date).format('YYYY-MM-DD');
        let result = [];

        $.each(resolved.slots, function (index, range) {
            let startText = self.utcToProjectHHmm(range.start_time, zoneId, day);
            let endText = self.utcToProjectHHmm(range.end_time, zoneId, day);

            let current = moment(day + ' ' + startText, 'YYYY-MM-DD HH:mm', true);
            let end = moment(day + ' ' + endText, 'YYYY-MM-DD HH:mm', true);

            if (!current.isValid() || !end.isValid()) return;

            if (!end.isAfter(current)) {
                end.add(1, 'days');
            }

            while (current.isBefore(end)) {
                let next = current.clone().add(period, 'minutes');
                result.push({range_index: index, start: current.clone(), end: next.clone()});
                current = next;
            }
        });

        return result;
    }
}

SSKeyHandoverScheduleService.$inject = ['SSDateService'];
app.service('SSKeyHandoverScheduleService', SSKeyHandoverScheduleService);
